import { zodResolver } from '@hookform/resolvers/zod';
import {
  pledgeStudyConfigControllerListMeasurementPoints,
  pledgeStudyConfigControllerListPledgeStudyTypes,
  pledgeStudyControllerCreate,
  pledgeStudyControllerDeleteAttachment,
  pledgeStudyControllerFindOne,
  pledgeStudyControllerUpdate,
} from '@kalba/api-client';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Download, Plus, Search, Trash2, UploadCloud } from 'lucide-react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import { z } from 'zod';
import { AppModal } from '../../../../components/ui/app-modal';
import { Button } from '../../../../components/ui/button';
import { Price } from '../../../../components/ui/price';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../../../components/ui/select';
import { api } from '../../../../lib/ky';
import { usePledgeStudyTabStore } from '../../../../stores/pledge-study-tab.store';
import type { PermitDetail } from './types';

interface Props {
  permit: PermitDetail;
  onUpdated: () => void;
  can: (perm: string) => boolean;
}

interface MeasurementPointType {
  id: string;
  name: string;
  code: string;
  unitPrice: string;
  status: string;
}

interface PledgeStudyTypeRecord {
  id: string;
  name: string;
  code: string;
  baseAmount: string;
  status: string;
}

interface FeeBreakdownJson {
  studyType: string;
  baseAmount: string;
  pointsTotal: string;
  authorityFixed: string;
  authorityOveragePct: string;
}

interface PledgeStudyWithUrls {
  id: string;
  pledgeStudyTypeId: string;
  pledgeStudyType?: { id: string; name: string } | null;
  status: string;
  totalFee?: string | null;
  beeahFee?: string | null;
  kalbaFee?: string | null;
  feeBreakdown?: FeeBreakdownJson | null;
  measurementPoints: Array<{
    id: string;
    measurementPointTypeId: string;
    measurementPointType?: { id: string; name: string; code: string } | null;
    unitPriceAtTime: string;
    quantity: number;
  }>;
  attachments: Array<{
    id: string;
    fileName: string;
    signedUrl?: string | null;
    uploadedAt: string;
  }>;
}

interface FeePreview {
  totalFee: string;
  beeahFee: string;
  authorityFee: string;
  feeBreakdown?: {
    studyType: string;
    baseAmount: string;
    pointsTotal: string;
    points: Array<{
      measurementPointTypeId: string;
      name: string;
      unitPriceAtTime: string;
      quantity: number;
      lineTotal: string;
    }>;
    authorityFixed: string;
    authorityOveragePct: string;
  };
}

function formatDate(iso: string) {
  try {
    return new Intl.DateTimeFormat('en-AE', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
    }).format(new Date(iso));
  } catch {
    return iso;
  }
}

function computeTotals(
  selected: Record<string, number>,
  mpTypes: MeasurementPointType[] | undefined,
  studyType: PledgeStudyTypeRecord | undefined,
) {
  const baseAmount = Number.parseFloat(studyType?.baseAmount ?? '0') || 0;
  const mpTotal = Object.entries(selected).reduce((sum, [id, qty]) => {
    const mp = mpTypes?.find((m) => m.id === id);
    return sum + (mp ? (Number.parseFloat(mp.unitPrice) || 0) * qty : 0);
  }, 0);
  return { baseAmount, mpTotal, grandTotal: baseAmount + mpTotal };
}

const pledgeStudySchema = z.object({
  typeId: z.string().min(1, 'Study type is required'),
  measurementPoints: z.record(z.number().min(1)),
});
type PledgeStudyFormValues = z.infer<typeof pledgeStudySchema>;

export function PledgeStudyTab({ permit, onUpdated, can }: Props) {
  const qc = useQueryClient();
  const psKey = ['pledgeStudy', permit.id];

  const { data: pledgeStudy, isLoading } = useQuery({
    queryKey: psKey,
    queryFn: () =>
      pledgeStudyControllerFindOne({
        permitId: permit.id,
      }) as unknown as Promise<PledgeStudyWithUrls>,
    enabled: !!permit.pledgeStudy,
    retry: false,
  });

  const { data: psTypes } = useQuery({
    queryKey: ['pledgeStudyTypes'],
    queryFn: () =>
      pledgeStudyConfigControllerListPledgeStudyTypes() as unknown as Promise<
        PledgeStudyTypeRecord[]
      >,
  });

  const { data: mpTypes } = useQuery({
    queryKey: ['measurementPointTypes'],
    queryFn: () =>
      pledgeStudyConfigControllerListMeasurementPoints() as unknown as Promise<
        MeasurementPointType[]
      >,
  });

  const { createOpen, addMPModalFor, setCreateOpen, setAddMPModalFor } = usePledgeStudyTabStore();

  // Once a pledge study exists, clean up create-mode store state.
  useEffect(() => {
    if (permit.pledgeStudy) {
      setCreateOpen(false);
    }
  }, [permit.pledgeStudy, setCreateOpen]);

  const createForm = useForm<PledgeStudyFormValues>({
    resolver: zodResolver(pledgeStudySchema),
    defaultValues: { typeId: '', measurementPoints: {} },
  });
  const createTypeId = createForm.watch('typeId');
  const createMPs = createForm.watch('measurementPoints') ?? {};

  const editInitialMPs: Record<string, number> = permit.pledgeStudy?.measurementPoints
    ? Object.fromEntries(
        permit.pledgeStudy.measurementPoints.map((mp) => [mp.measurementPointTypeId, mp.quantity]),
      )
    : {};

  const editForm = useForm<PledgeStudyFormValues>({
    resolver: zodResolver(pledgeStudySchema),
    defaultValues: {
      typeId: permit.pledgeStudy?.pledgeStudyTypeId ?? '',
      measurementPoints: editInitialMPs,
    },
  });
  const editTypeId = editForm.watch('typeId');
  const editMPs = editForm.watch('measurementPoints') ?? {};

  // Sync editForm when pledge study is first linked (e.g. immediately after first-time create).
  // defaultValues are only applied at mount — when permit.pledgeStudy was null — so we must
  // explicitly reset once the pledge study ID appears.
  const psId = permit.pledgeStudy?.id;
  // biome-ignore lint/correctness/useExhaustiveDependencies: only sync on id change, not every render
  useEffect(() => {
    if (!permit.pledgeStudy) return;
    const mps = Object.fromEntries(
      permit.pledgeStudy.measurementPoints.map((mp) => [mp.measurementPointTypeId, mp.quantity]),
    );
    editForm.reset({ typeId: permit.pledgeStudy.pledgeStudyTypeId, measurementPoints: mps });
  }, [psId]);

  // Debounced states for fee preview
  const [debouncedCreate, setDebouncedCreate] = useState({
    typeId: '',
    mps: {} as Record<string, number>,
  });
  const [debouncedEdit, setDebouncedEdit] = useState({ typeId: editTypeId, mps: editMPs });
  const createDebRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const editDebRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // biome-ignore lint/correctness/useExhaustiveDependencies: JSON.stringify for deep compare
  useEffect(() => {
    if (createDebRef.current) clearTimeout(createDebRef.current);
    createDebRef.current = setTimeout(
      () => setDebouncedCreate({ typeId: createTypeId, mps: createMPs }),
      500,
    );
    return () => {
      if (createDebRef.current) clearTimeout(createDebRef.current);
    };
  }, [createTypeId, JSON.stringify(createMPs)]);

  // biome-ignore lint/correctness/useExhaustiveDependencies: JSON.stringify for deep compare
  useEffect(() => {
    if (editDebRef.current) clearTimeout(editDebRef.current);
    editDebRef.current = setTimeout(
      () => setDebouncedEdit({ typeId: editTypeId, mps: editMPs }),
      500,
    );
    return () => {
      if (editDebRef.current) clearTimeout(editDebRef.current);
    };
  }, [editTypeId, JSON.stringify(editMPs)]);

  const createInputsDirty =
    createTypeId !== debouncedCreate.typeId ||
    JSON.stringify(createMPs) !== JSON.stringify(debouncedCreate.mps);
  const editInputsDirty =
    editTypeId !== debouncedEdit.typeId ||
    JSON.stringify(editMPs) !== JSON.stringify(debouncedEdit.mps);

  const createFeePreview = useQuery({
    queryKey: [
      'pledgeFeePreview',
      permit.id,
      'create',
      debouncedCreate.typeId,
      JSON.stringify(debouncedCreate.mps),
    ],
    queryFn: () =>
      api
        .post(`permits/${permit.id}/pledge-study/fee-preview`, {
          json: {
            pledgeStudyTypeId: debouncedCreate.typeId,
            measurementPoints: Object.entries(debouncedCreate.mps).map(([id, qty]) => ({
              measurementPointTypeId: id,
              quantity: qty,
            })),
          },
        })
        .json<FeePreview>(),
    enabled: !!debouncedCreate.typeId && !createInputsDirty,
  });

  const editFeePreview = useQuery({
    queryKey: [
      'pledgeFeePreview',
      permit.id,
      'edit',
      debouncedEdit.typeId,
      JSON.stringify(debouncedEdit.mps),
    ],
    queryFn: () =>
      api
        .post(`permits/${permit.id}/pledge-study/fee-preview`, {
          json: {
            pledgeStudyTypeId: debouncedEdit.typeId,
            measurementPoints: Object.entries(debouncedEdit.mps)
              .filter(([, q]) => q > 0)
              .map(([id, qty]) => ({ measurementPointTypeId: id, quantity: qty })),
          },
        })
        .json<FeePreview>(),
    enabled: !!debouncedEdit.typeId && permit.pledgeStudy?.status === 'DRAFT' && !editInputsDirty,
  });

  const createMutation = useMutation({
    mutationFn: () => {
      const { typeId, measurementPoints } = createForm.getValues();
      return pledgeStudyControllerCreate({
        permitId: permit.id,
        requestBody: {
          pledgeStudyTypeId: typeId,
          measurementPoints: Object.entries(measurementPoints).map(([id, qty]) => ({
            measurementPointTypeId: id,
            quantity: qty,
          })),
        },
      });
    },
    onSuccess: () => {
      setAddMPModalFor(null);
      qc.invalidateQueries({ queryKey: psKey });
      onUpdated();
      // Do NOT close/reset the form here — permit.pledgeStudy is still null until
      // the parent permit query refetches. Once it refetches the edit view renders
      // automatically and the create branch is never entered.
    },
  });

  const updateMutation = useMutation({
    mutationFn: () => {
      const { typeId, measurementPoints } = editForm.getValues();
      return pledgeStudyControllerUpdate({
        permitId: permit.id,
        requestBody: {
          pledgeStudyTypeId: typeId,
          measurementPoints: Object.entries(measurementPoints)
            .filter(([, q]) => q > 0)
            .map(([id, qty]) => ({ measurementPointTypeId: id, quantity: qty })),
        },
      });
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: psKey });
      onUpdated();
    },
  });

  const uploadAttachment = useMutation({
    mutationFn: async (file: File) => {
      const fd = new FormData();
      fd.append('file', file);
      await api.post(`requests/${permit.id}/pledge-study/attachments`, { body: fd }).json();
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: psKey });
      onUpdated();
    },
  });

  const deleteAttachment = useMutation({
    mutationFn: (attId: string) =>
      pledgeStudyControllerDeleteAttachment({ permitId: permit.id, attachmentId: attId }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: psKey });
      onUpdated();
    },
  });

  function addMP(id: string, mode: 'create' | 'edit') {
    const form = mode === 'create' ? createForm : editForm;
    const current = form.getValues('measurementPoints');
    form.setValue('measurementPoints', { ...current, [id]: 1 });
    setAddMPModalFor(null);
  }

  function removeMP(id: string, mode: 'create' | 'edit') {
    const form = mode === 'create' ? createForm : editForm;
    const current = { ...form.getValues('measurementPoints') };
    delete current[id];
    form.setValue('measurementPoints', current);
  }

  function setQty(id: string, qty: number, mode: 'create' | 'edit') {
    const form = mode === 'create' ? createForm : editForm;
    const safe = Math.max(1, qty || 1);
    const current = form.getValues('measurementPoints');
    form.setValue('measurementPoints', { ...current, [id]: safe });
  }

  // ── No pledge study yet ─────────────────────────────────────────────────

  if (!permit.pledgeStudy) {
    if (permit.status === 'PLEDGE_STUDY_REQUEST' && can('pledge:create')) {
      if (!createOpen) {
        return (
          <div className="bg-card rounded-2xl border border-border shadow-sm p-12 text-center">
            <p className="mb-4 text-sm text-muted-foreground">No Pledge & Study created yet.</p>
            <Button variant="primary" size="sm" onClick={() => setCreateOpen(true)}>
              Create Pledge & Study
            </Button>
          </div>
        );
      }

      const createType = psTypes?.find((t) => t.id === createTypeId);
      const { baseAmount, mpTotal, grandTotal } = computeTotals(createMPs, mpTypes, createType);

      return (
        <>
          <div className="bg-card rounded-2xl border border-border shadow-sm p-6 space-y-6">
            <h3 className="text-sm font-semibold text-gray-900">Create Pledge & Study</h3>

            {/* Study Type */}
            <div>
              <p className="mb-1.5 text-xs font-medium text-gray-500">
                Study Type <span className="text-red-500">*</span>
              </p>
              <Select
                value={createTypeId || undefined}
                onValueChange={(v) => createForm.setValue('typeId', v)}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select study type…" />
                </SelectTrigger>
                <SelectContent>
                  {psTypes?.map((t) => (
                    <SelectItem key={t.id} value={t.id}>
                      {t.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {createType && (
                <p className="mt-1 text-xs text-gray-400">
                  Study Type Cost:{' '}
                  <span className="font-medium text-gray-600">
                    <Price value={createType.baseAmount} iconClassName="w-[0.8em] h-[0.8em]" />
                  </span>
                </p>
              )}
            </div>

            {/* Measurement Points */}
            <div>
              <div className="mb-2 flex items-center justify-between">
                <p className="text-xs font-medium text-gray-500">Measurement Points</p>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setAddMPModalFor('create')}
                  className="h-7 gap-1 text-xs"
                >
                  <Plus size={12} />
                  Add Point
                </Button>
              </div>
              <MeasurementPointsTable
                mpTypes={mpTypes}
                selected={createMPs}
                onRemove={(id) => removeMP(id, 'create')}
                onQtyChange={(id, qty) => setQty(id, qty, 'create')}
                baseAmount={baseAmount}
                mpTotal={mpTotal}
                grandTotal={grandTotal}
              />
            </div>

            {/* Fee Preview (debounced) */}
            {(createInputsDirty || createFeePreview.isFetching) && !!createTypeId && (
              <div className="flex items-center gap-2 text-xs text-gray-400">
                <span className="inline-block h-3 w-3 animate-spin rounded-full border-2 border-gray-300 border-t-green-500" />
                Computing fees…
              </div>
            )}
            {createFeePreview.data && !createInputsDirty && (
              <FeePreviewCard data={createFeePreview.data} />
            )}

            {createMutation.isError && (
              <p className="text-xs text-red-500">Failed to create pledge study.</p>
            )}

            <div className="flex gap-2">
              <Button
                variant="primary"
                size="sm"
                disabled={!createTypeId || createMutation.isPending}
                onClick={() => createMutation.mutate()}
              >
                {createMutation.isPending ? 'Saving…' : 'Save'}
              </Button>
              <Button variant="ghost" size="sm" onClick={() => setCreateOpen(false)}>
                Cancel
              </Button>
            </div>
          </div>
          <AddMPModal
            open={addMPModalFor !== null}
            onClose={() => setAddMPModalFor(null)}
            mpTypes={mpTypes ?? []}
            alreadySelected={Object.keys(createMPs)}
            onSelect={(id) => addMP(id, 'create')}
          />
        </>
      );
    }

    return (
      <div className="bg-card rounded-2xl border border-border shadow-sm p-12 text-center text-sm text-muted-foreground">
        No Pledge & Study record.
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="bg-card rounded-2xl border border-border shadow-sm p-6 space-y-4">
        {(['s1', 's2', 's3'] as const).map((k) => (
          <div key={k} className="h-16 rounded-xl bg-muted animate-pulse" />
        ))}
      </div>
    );
  }

  const isDraft = permit.pledgeStudy.status === 'DRAFT';

  const editType = psTypes?.find((t) => t.id === editTypeId);
  const {
    baseAmount: editBaseAmount,
    mpTotal: editMPsTotal,
    grandTotal: editGrandTotal,
  } = computeTotals(editMPs, mpTypes, editType);

  const displayMPs = pledgeStudy?.measurementPoints ?? permit.pledgeStudy.measurementPoints;

  return (
    <div className="space-y-6">
      {/* Main card */}
      <div className="bg-card rounded-2xl border border-border shadow-sm p-6">
        <div className="mb-6 flex items-start justify-between">
          <h3 className="text-sm font-semibold text-gray-900">Pledge & Study</h3>
          {!isDraft && pledgeStudy?.totalFee && (
            <div className="text-right">
              <p className="text-xs text-gray-400 mb-0.5">Total Pledge & Study Fee</p>
              <p className="text-2xl font-bold text-gray-900">
                <Price value={pledgeStudy.totalFee} />
              </p>
            </div>
          )}
        </div>

        <div className="space-y-6">
          {/* Study Type */}
          <div>
            <p className="mb-1.5 text-xs font-medium text-gray-500">Study Type</p>
            {isDraft ? (
              <>
                <Select
                  value={editTypeId || undefined}
                  onValueChange={(v) => editForm.setValue('typeId', v)}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select type…" />
                  </SelectTrigger>
                  <SelectContent>
                    {psTypes?.map((t) => (
                      <SelectItem key={t.id} value={t.id}>
                        {t.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {editType && (
                  <p className="mt-1 text-xs text-gray-400">
                    Study Type Cost:{' '}
                    <span className="font-medium text-gray-600">
                      <Price value={editType.baseAmount} iconClassName="w-[0.8em] h-[0.8em]" />
                    </span>
                  </p>
                )}
              </>
            ) : (
              <p className="text-sm font-medium text-gray-900">
                {pledgeStudy?.pledgeStudyType?.name ?? permit.pledgeStudy.pledgeStudyTypeId}
              </p>
            )}
          </div>

          {/* Measurement Points */}
          <div>
            {isDraft ? (
              <>
                <div className="mb-2 flex items-center justify-between">
                  <p className="text-xs font-medium text-gray-500">Measurement Points</p>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setAddMPModalFor('edit')}
                    className="h-7 gap-1 text-xs"
                  >
                    <Plus size={12} />
                    Add Point
                  </Button>
                </div>
                <MeasurementPointsTable
                  mpTypes={mpTypes}
                  selected={editMPs}
                  onRemove={(id) => removeMP(id, 'edit')}
                  onQtyChange={(id, qty) => setQty(id, qty, 'edit')}
                  baseAmount={editBaseAmount}
                  mpTotal={editMPsTotal}
                  grandTotal={editGrandTotal}
                />
              </>
            ) : (
              <>
                <p className="mb-2 text-xs font-medium text-gray-500">Measurement Points</p>
                {displayMPs.length === 0 ? (
                  <p className="text-xs text-gray-400">No measurement points.</p>
                ) : (
                  <ReadonlyMeasurementPointsTable measurementPoints={displayMPs} />
                )}
              </>
            )}
          </div>

          {/* Fee Preview (draft) */}
          {isDraft && (editInputsDirty || editFeePreview.isFetching) && !!editTypeId && (
            <div className="flex items-center gap-2 text-xs text-gray-400">
              <span className="inline-block h-3 w-3 animate-spin rounded-full border-2 border-gray-300 border-t-green-500" />
              Computing fees…
            </div>
          )}
          {isDraft && editFeePreview.data && !editInputsDirty && (
            <FeePreviewCard data={editFeePreview.data} />
          )}

          {/* Frozen fee breakdown (submitted/approved) */}
          {!isDraft && pledgeStudy?.feeBreakdown && (
            <div className="rounded-xl border border-gray-200 bg-gray-50 p-4 space-y-2">
              <p className="text-xs font-semibold uppercase tracking-wide text-gray-500">
                Fee Breakdown
              </p>
              <div className="flex items-center justify-between">
                <span className="text-xs text-gray-500">Study fee</span>
                <span className="text-xs font-medium text-gray-700">
                  <Price value={pledgeStudy.feeBreakdown.baseAmount} />
                </span>
              </div>
              {Number(pledgeStudy.feeBreakdown.pointsTotal) > 0 && (
                <div className="flex items-center justify-between">
                  <span className="text-xs text-gray-500">Measurement points fee</span>
                  <span className="text-xs font-medium text-gray-700">
                    <Price value={pledgeStudy.feeBreakdown.pointsTotal} />
                  </span>
                </div>
              )}
              <div className="border-t border-gray-200 pt-2 flex items-center justify-between">
                <span className="text-xs font-semibold text-gray-700">
                  Total Pledge & Study Fee
                </span>
                <span className="text-sm font-bold text-gray-900">
                  <Price value={pledgeStudy.totalFee ?? '0'} />
                </span>
              </div>
              {pledgeStudy.beeahFee && (
                <div className="flex items-center justify-between">
                  <span className="text-xs text-gray-400">Beeah consultant fee</span>
                  <span className="text-xs font-medium text-gray-600">
                    <Price value={pledgeStudy.beeahFee} />
                  </span>
                </div>
              )}
            </div>
          )}

          {/* Draft actions */}
          {isDraft && (
            <div className="flex gap-2">
              <Button
                variant="primary"
                size="sm"
                onClick={() => updateMutation.mutate()}
                disabled={updateMutation.isPending}
              >
                {updateMutation.isPending ? 'Saving…' : 'Save'}
              </Button>
            </div>
          )}
        </div>
      </div>

      {/* Attachments */}
      {pledgeStudy && (
        <AttachmentsSection
          attachments={pledgeStudy.attachments}
          isDraft={isDraft}
          canEdit={can('pledge:create')}
          onUpload={(file) => uploadAttachment.mutate(file)}
          onDelete={(id) => deleteAttachment.mutate(id)}
          uploading={uploadAttachment.isPending}
          uploadError={uploadAttachment.isError ? 'Upload failed. Try again.' : null}
        />
      )}

      {/* Add Measurement Point Modal */}
      <AddMPModal
        open={addMPModalFor !== null}
        onClose={() => setAddMPModalFor(null)}
        mpTypes={mpTypes ?? []}
        alreadySelected={addMPModalFor === 'create' ? Object.keys(createMPs) : Object.keys(editMPs)}
        onSelect={(id) => addMPModalFor && addMP(id, addMPModalFor)}
      />
    </div>
  );
}

// ── MeasurementPointsTable (editable) ─────────────────────────────────────

function MeasurementPointsTable({
  mpTypes,
  selected,
  onRemove,
  onQtyChange,
  baseAmount,
  mpTotal,
  grandTotal,
}: {
  mpTypes?: MeasurementPointType[];
  selected: Record<string, number>;
  onRemove: (id: string) => void;
  onQtyChange: (id: string, qty: number) => void;
  baseAmount: number;
  mpTotal: number;
  grandTotal: number;
}) {
  const selectedIds = Object.keys(selected);

  if (selectedIds.length === 0) {
    return (
      <div className="rounded-xl border-2 border-dashed border-gray-200 p-6 text-center">
        <p className="text-xs text-gray-400">
          No measurement points added yet. Click &quot;Add Point&quot; to add one.
        </p>
      </div>
    );
  }

  return (
    <div className="overflow-hidden rounded-xl border border-gray-200">
      <table className="w-full text-sm">
        <thead>
          <tr className="bg-gray-50 text-left text-xs text-gray-500">
            <th className="px-3 py-2.5 font-medium">Measurement Point</th>
            <th className="px-3 py-2.5 font-medium text-right w-28">Quantity</th>
            <th className="px-3 py-2.5 font-medium text-right">Unit Price</th>
            <th className="px-3 py-2.5 font-medium text-right">Total Cost</th>
            <th className="w-8 px-2 py-2.5" />
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-100">
          {selectedIds.map((mpId) => {
            const mp = mpTypes?.find((m) => m.id === mpId);
            const qty = selected[mpId] ?? 1;
            const unitPrice = Number.parseFloat(mp?.unitPrice ?? '0') || 0;
            const rowTotal = unitPrice * qty;
            return (
              <tr key={mpId} className="hover:bg-gray-50/50 transition-colors">
                <td className="px-3 py-2 text-gray-800">
                  <div>
                    <p className="font-medium">{mp?.name ?? mpId}</p>
                    {mp?.code && <p className="text-xs text-gray-400">{mp.code}</p>}
                  </div>
                </td>
                <td className="px-3 py-2 text-right">
                  <input
                    type="number"
                    min={1}
                    value={qty}
                    onChange={(e) => onQtyChange(mpId, Number(e.target.value))}
                    className="h-8 w-24 rounded-lg border border-gray-300 bg-white px-2 text-right text-sm text-gray-900 hover:border-gray-400 focus:border-brand-400 focus:outline-none focus:ring-2 focus:ring-brand-100 transition-all"
                  />
                </td>
                <td className="px-3 py-2 text-right text-gray-500 text-xs">
                  <Price value={mp?.unitPrice ?? '0'} />
                </td>
                <td className="px-3 py-2 text-right font-semibold text-gray-800">
                  <Price value={rowTotal} />
                </td>
                <td className="px-2 py-2">
                  <button
                    type="button"
                    onClick={() => onRemove(mpId)}
                    className="rounded p-1 text-red-400 hover:bg-red-50 hover:text-red-600 transition-colors"
                    title="Remove"
                  >
                    <Trash2 size={13} />
                  </button>
                </td>
              </tr>
            );
          })}
        </tbody>
        <tfoot className="border-t border-gray-200">
          <tr className="bg-gray-50/80">
            <td colSpan={3} className="px-3 py-2 text-right text-xs text-gray-500">
              Study Type Cost
            </td>
            <td className="px-3 py-2 text-right text-xs font-medium text-gray-700">
              <Price value={baseAmount} />
            </td>
            <td />
          </tr>
          <tr className="bg-gray-50/80">
            <td colSpan={3} className="px-3 py-2 text-right text-xs text-gray-500">
              Measurement Points Total
            </td>
            <td className="px-3 py-2 text-right text-xs font-medium text-gray-700">
              <Price value={mpTotal} />
            </td>
            <td />
          </tr>
          <tr className="border-t-2 border-gray-300 bg-gray-100">
            <td colSpan={3} className="px-3 py-2.5 text-right text-xs font-semibold text-gray-700">
              Total Pledge & Study Cost
            </td>
            <td className="px-3 py-2.5 text-right text-sm font-bold text-gray-900">
              <Price value={grandTotal} />
            </td>
            <td />
          </tr>
        </tfoot>
      </table>
    </div>
  );
}

// ── ReadonlyMeasurementPointsTable ────────────────────────────────────────

function ReadonlyMeasurementPointsTable({
  measurementPoints,
}: {
  measurementPoints: Array<{
    id: string;
    measurementPointTypeId: string;
    measurementPointType?: { id: string; name: string; code: string } | null;
    unitPriceAtTime: string;
    quantity: number;
  }>;
}) {
  const total = measurementPoints.reduce(
    (sum, mp) => sum + (Number.parseFloat(mp.unitPriceAtTime) || 0) * mp.quantity,
    0,
  );

  return (
    <div className="overflow-hidden rounded-xl border border-gray-200">
      <table className="w-full text-sm">
        <thead>
          <tr className="bg-gray-50 text-left text-xs text-gray-500">
            <th className="px-3 py-2.5 font-medium">Measurement Point</th>
            <th className="px-3 py-2.5 font-medium text-right">Qty</th>
            <th className="px-3 py-2.5 font-medium text-right">Unit Price</th>
            <th className="px-3 py-2.5 font-medium text-right">Total Cost</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-100">
          {measurementPoints.map((mp) => {
            const rowTotal = (Number.parseFloat(mp.unitPriceAtTime) || 0) * mp.quantity;
            return (
              <tr key={mp.id} className="hover:bg-gray-50/50">
                <td className="px-3 py-2 text-gray-800">
                  <div>
                    <p className="font-medium">
                      {mp.measurementPointType?.name ?? mp.measurementPointTypeId}
                    </p>
                    {mp.measurementPointType?.code && (
                      <p className="text-xs text-gray-400">{mp.measurementPointType.code}</p>
                    )}
                  </div>
                </td>
                <td className="px-3 py-2 text-right text-gray-600">{mp.quantity}</td>
                <td className="px-3 py-2 text-right text-gray-500 text-xs">
                  <Price value={mp.unitPriceAtTime} />
                </td>
                <td className="px-3 py-2 text-right font-semibold text-gray-800">
                  <Price value={rowTotal} />
                </td>
              </tr>
            );
          })}
        </tbody>
        <tfoot className="border-t-2 border-gray-200">
          <tr className="bg-gray-50">
            <td colSpan={3} className="px-3 py-2.5 text-right text-xs font-semibold text-gray-600">
              Measurement Points Total
            </td>
            <td className="px-3 py-2.5 text-right text-sm font-bold text-gray-900">
              <Price value={total} />
            </td>
          </tr>
        </tfoot>
      </table>
    </div>
  );
}

// ── AddMPModal ─────────────────────────────────────────────────────────────

function AddMPModal({
  open,
  onClose,
  mpTypes,
  alreadySelected,
  onSelect,
}: {
  open: boolean;
  onClose: () => void;
  mpTypes: MeasurementPointType[];
  alreadySelected: string[];
  onSelect: (id: string) => void;
}) {
  const [search, setSearch] = useState('');

  const available = mpTypes.filter(
    (m) => !alreadySelected.includes(m.id) && m.status !== 'INACTIVE',
  );
  const filtered = available.filter(
    (m) =>
      m.name.toLowerCase().includes(search.toLowerCase()) ||
      m.code.toLowerCase().includes(search.toLowerCase()),
  );

  function handleClose() {
    setSearch('');
    onClose();
  }

  function handleSelect(id: string) {
    setSearch('');
    onSelect(id);
  }

  return (
    <AppModal
      open={open}
      onClose={handleClose}
      title="Add Measurement Point"
      description="Select a measurement point to add to this study."
      maxWidth="max-w-lg"
    >
      <div className="space-y-3">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-gray-400" />
          <input
            type="text"
            placeholder="Search by name or code…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="w-full rounded-xl border border-gray-200 bg-white pl-8 pr-3 py-2.5 text-sm placeholder-gray-400 focus:border-brand-400 focus:outline-none focus:ring-2 focus:ring-brand-100 transition-all"
            // biome-ignore lint/a11y/noAutofocus: intentional focus for modal search
            autoFocus
          />
        </div>

        {filtered.length === 0 ? (
          <p className="py-6 text-center text-sm text-gray-400">
            {available.length === 0
              ? 'All measurement points have already been added.'
              : 'No results matching your search.'}
          </p>
        ) : (
          <div className="max-h-80 overflow-y-auto divide-y divide-gray-100 rounded-xl border border-gray-200">
            {filtered.map((mp) => (
              <button
                key={mp.id}
                type="button"
                onClick={() => handleSelect(mp.id)}
                className="w-full flex items-center justify-between px-4 py-3 text-left hover:bg-brand-50 transition-colors"
              >
                <div>
                  <p className="text-sm font-medium text-gray-800">{mp.name}</p>
                  <p className="text-xs text-gray-400 mt-0.5">{mp.code}</p>
                </div>
                <div className="shrink-0 text-right ml-4">
                  <p className="text-sm font-semibold text-gray-700">
                    <Price value={mp.unitPrice} />
                  </p>
                  <p className="text-xs text-gray-400">per unit</p>
                </div>
              </button>
            ))}
          </div>
        )}
      </div>
    </AppModal>
  );
}

// ── AttachmentsSection ─────────────────────────────────────────────────────

const ALLOWED_MIME = ['application/pdf', 'image/jpeg', 'image/png'];
const MAX_UPLOAD_BYTES = 10 * 1024 * 1024;

function AttachmentsSection({
  attachments,
  isDraft,
  canEdit,
  onUpload,
  onDelete,
  uploading,
  uploadError,
}: {
  attachments: PledgeStudyWithUrls['attachments'];
  isDraft: boolean;
  canEdit: boolean;
  onUpload: (file: File) => void;
  onDelete: (id: string) => void;
  uploading: boolean;
  uploadError: string | null;
}) {
  const [dragging, setDragging] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState<string | null>(null);
  const [fileError, setFileError] = useState<string | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const validateAndUpload = useCallback(
    (file: File) => {
      setFileError(null);
      if (!ALLOWED_MIME.includes(file.type)) {
        setFileError('Only PDF, JPG, and PNG files are allowed.');
        return;
      }
      if (file.size > MAX_UPLOAD_BYTES) {
        setFileError('File size must be 10 MB or less.');
        return;
      }
      onUpload(file);
    },
    [onUpload],
  );

  // Paste-to-upload support
  useEffect(() => {
    if (!isDraft || !canEdit) return;
    function handlePaste(e: ClipboardEvent) {
      const files = Array.from(e.clipboardData?.files ?? []);
      for (const f of files) validateAndUpload(f);
    }
    document.addEventListener('paste', handlePaste);
    return () => document.removeEventListener('paste', handlePaste);
  }, [isDraft, canEdit, validateAndUpload]);

  function handleDrop(e: React.DragEvent) {
    e.preventDefault();
    setDragging(false);
    for (const f of Array.from(e.dataTransfer.files)) validateAndUpload(f);
  }

  function handleInputChange(e: React.ChangeEvent<HTMLInputElement>) {
    for (const f of Array.from(e.target.files ?? [])) validateAndUpload(f);
    e.target.value = '';
  }

  return (
    <div className="bg-card rounded-2xl border border-border shadow-sm p-6">
      <h3 className="mb-4 text-xs font-semibold uppercase tracking-wide text-gray-500">
        Attachments
      </h3>

      {/* Drop zone (draft only) */}
      {isDraft && canEdit && (
        // biome-ignore lint/a11y/useKeyWithClickEvents: drop zone click handled via hidden input
        <div
          className={[
            'mb-4 cursor-pointer rounded-xl border-2 border-dashed p-6 text-center transition-colors select-none',
            dragging
              ? 'border-brand-400 bg-brand-50'
              : 'border-gray-200 hover:border-brand-300 hover:bg-gray-50/50',
            uploading ? 'pointer-events-none opacity-50' : '',
          ].join(' ')}
          onClick={() => inputRef.current?.click()}
          onDragOver={(e) => {
            e.preventDefault();
            setDragging(true);
          }}
          onDragLeave={() => setDragging(false)}
          onDrop={handleDrop}
        >
          <UploadCloud className="mx-auto mb-2 h-7 w-7 text-gray-300" />
          <p className="text-sm font-medium text-gray-500">
            {uploading ? 'Uploading…' : 'Drag & drop, click to browse, or paste'}
          </p>
          <p className="mt-1 text-xs text-gray-400">PDF, JPG, PNG · Max 10 MB</p>
          <input
            ref={inputRef}
            type="file"
            accept=".pdf,.jpg,.jpeg,.png"
            multiple
            className="sr-only"
            onChange={handleInputChange}
          />
        </div>
      )}

      {fileError && <p className="mb-3 text-xs text-red-500">{fileError}</p>}
      {uploadError && <p className="mb-3 text-xs text-red-500">{uploadError}</p>}

      {attachments.length === 0 ? (
        <p className="py-4 text-center text-xs text-gray-400">No attachments uploaded yet.</p>
      ) : (
        <div className="overflow-hidden rounded-xl border border-gray-200">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-gray-50 text-left text-xs text-gray-500">
                <th className="px-3 py-2.5 font-medium">File Name</th>
                <th className="px-3 py-2.5 font-medium">Upload Date</th>
                <th className="px-3 py-2.5 font-medium text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {attachments.map((att) => (
                <tr key={att.id} className="hover:bg-gray-50/50 transition-colors">
                  <td className="px-3 py-2">
                    <a
                      href={att.signedUrl ?? '#'}
                      target="_blank"
                      rel="noreferrer"
                      className="font-medium text-brand-600 hover:text-brand-700 hover:underline"
                    >
                      {att.fileName}
                    </a>
                  </td>
                  <td className="px-3 py-2 text-xs text-gray-400">{formatDate(att.uploadedAt)}</td>
                  <td className="px-3 py-2">
                    <div className="flex items-center justify-end gap-1">
                      <a
                        href={att.signedUrl ?? '#'}
                        target="_blank"
                        rel="noreferrer"
                        className="rounded p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-600 transition-colors"
                        title="Download"
                      >
                        <Download size={13} />
                      </a>
                      {isDraft &&
                        canEdit &&
                        (deleteConfirm === att.id ? (
                          <span className="flex items-center gap-1 text-xs ml-1">
                            <span className="text-gray-500">Delete?</span>
                            <button
                              type="button"
                              onClick={() => {
                                onDelete(att.id);
                                setDeleteConfirm(null);
                              }}
                              className="font-medium text-red-600 hover:text-red-700"
                            >
                              Yes
                            </button>
                            <button
                              type="button"
                              onClick={() => setDeleteConfirm(null)}
                              className="text-gray-500 hover:text-gray-700"
                            >
                              No
                            </button>
                          </span>
                        ) : (
                          <button
                            type="button"
                            onClick={() => setDeleteConfirm(att.id)}
                            className="rounded p-1 text-red-400 hover:bg-red-50 hover:text-red-600 transition-colors"
                            title="Delete"
                          >
                            <Trash2 size={13} />
                          </button>
                        ))}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// ── FeePreviewCard ─────────────────────────────────────────────────────────

function FeePreviewCard({ data }: { data: FeePreview }) {
  return (
    <div className="overflow-hidden rounded-xl border border-green-100 bg-green-50">
      <div className="flex items-center justify-between border-b border-green-100 px-4 py-3">
        <span className="text-xs font-semibold uppercase tracking-wide text-green-700">
          Fee Preview
        </span>
        <span className="text-sm font-bold text-green-800">
          <Price value={data.totalFee} />
        </span>
      </div>
      <div className="flex gap-6 border-b border-green-100 bg-white px-4 py-2 text-xs text-gray-600">
        <span>
          Beeah:{' '}
          <span className="font-medium">
            <Price value={data.beeahFee} />
          </span>
        </span>
        <span>
          Kalba:{' '}
          <span className="font-medium">
            <Price value={data.authorityFee} />
          </span>
        </span>
      </div>
      {data.feeBreakdown && data.feeBreakdown.points.length > 0 && (
        <div className="divide-y divide-green-50">
          {data.feeBreakdown.points.map((pt) => (
            <div
              key={pt.measurementPointTypeId}
              className="flex items-center justify-between px-4 py-2 text-xs"
            >
              <span className="text-gray-600">{pt.name}</span>
              <div className="flex items-center gap-4 text-gray-500">
                <span>×{pt.quantity}</span>
                <span>
                  <Price value={pt.unitPriceAtTime} />
                </span>
                <span className="font-medium text-gray-700">
                  <Price value={pt.lineTotal} />
                </span>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
